import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

import { useRawEntityQuery, useSaveRawEntityMutation } from "../hooks/admin-queries"
import { RawJsonTab } from "./shared/raw-json-tab"

type EditAdminGroupDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  groupKey: string | null
  groupLabel: string
  /** Callback to open the structured form dialog (existing AccessPolicyDialog). */
  onOpenStructuredEditor?: () => void
}

/**
 * Per-group edit dialog with Form + Raw JSON tabs.
 *
 * The Form tab is a light wrapper around the existing structured
 * ``AccessPolicyDialog`` flow: it explains that the structured editor lives
 * in its own dialog and gives the admin a button to open it. The Raw tab
 * is the usual raw-JSON editor backed by
 * ``/api/admin/admin-groups/<key>/raw``.
 *
 * We chose this split rather than inlining the full access-policy UI to
 * keep the existing "Configure" flow untouched while still offering a Raw
 * tab matching the treatment of the other admin entities.
 */
export function EditAdminGroupDialog({
  isOpen,
  onOpenChange,
  groupKey,
  groupLabel,
  onOpenStructuredEditor,
}: EditAdminGroupDialogProps) {
  const { t } = useTranslation()

  const endpoint = groupKey ? `/admin/admin-groups/${encodeURIComponent(groupKey)}/raw` : null

  const { data: rawData, refetch } = useRawEntityQuery<Record<string, unknown>>(
    endpoint,
    isOpen && !!endpoint
  )

  const saveMutation = useSaveRawEntityMutation<Record<string, unknown>>({
    endpoint: endpoint ?? "",
    invalidateOnSuccess: [["admin", "admin-groups"]],
  })

  const stored = useMemo(() => rawData?.entity ?? {}, [rawData])
  const hash = rawData?.hash ?? ""
  const [resetSignal, setResetSignal] = useState(0)

  useEffect(() => {
    setResetSignal((n) => n + 1)
  }, [stored])

  const handleRawSave = async (next: unknown, reason: string) => {
    if (!groupKey || !endpoint || !hash) return
    const parsed = (next || {}) as Record<string, unknown>
    try {
      await saveMutation.mutateAsync({
        entity: parsed,
        expectedHash: hash,
        reason,
        source: "raw",
      })
      void refetch()
      toast.success(
        t("adminDashboard.adminGroupEdit.toastSavedRaw", {
          defaultValue: "Admin group saved (raw)",
        })
      )
      onOpenChange(false)
    } catch (err) {
      toast.error(
        t("adminDashboard.adminGroupEdit.toastRawSaveFailed", {
          defaultValue: "Raw save failed",
        }),
        { description: err instanceof Error ? err.message : undefined }
      )
      throw err
    }
  }

  if (!groupKey) return null

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[760px]">
        <DialogHeader>
          <DialogTitle>
            {t("adminDashboard.adminGroupEdit.title", { defaultValue: "Edit admin group" })}{" "}
            <code className="font-mono text-sm">{groupKey}</code>{" "}
            <span className="text-muted-foreground text-sm font-normal">({groupLabel})</span>
          </DialogTitle>
          <DialogDescription>
            {t("adminDashboard.adminGroupEdit.description", {
              defaultValue:
                "The Form tab uses the structured access-policy editor. The Raw tab shows the full stored admin-group JSON.",
            })}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="form">
          <TabsList>
            <TabsTrigger value="form">
              {t("adminDashboard.adminGroupEdit.tabForm", { defaultValue: "Form" })}
            </TabsTrigger>
            <TabsTrigger
              className="text-destructive data-[state=active]:text-destructive"
              value="raw"
            >
              {t("adminDashboard.adminGroupEdit.tabRaw", { defaultValue: "Raw JSON" })}
            </TabsTrigger>
          </TabsList>

          <TabsContent className="mt-4" value="form">
            <div className="space-y-4 rounded-lg border p-4">
              <p className="text-muted-foreground text-sm">
                {t("adminDashboard.adminGroupEdit.formHelp", {
                  defaultValue:
                    "Use the structured access-policy editor to add or remove users and groups from this admin role.",
                })}
              </p>
              {onOpenStructuredEditor ? (
                <Button
                  type="button"
                  onClick={() => {
                    onOpenStructuredEditor()
                    onOpenChange(false)
                  }}
                >
                  {t("adminDashboard.adminGroupEdit.openStructured", {
                    defaultValue: "Open structured editor",
                  })}
                </Button>
              ) : null}
            </div>
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("adminDashboard.adminGroupEdit.cancel", { defaultValue: "Close" })}
              </Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent className="mt-4" value="raw">
            <RawJsonTab
              confirmToken={groupKey}
              isPending={saveMutation.isPending || !hash}
              resetSignal={resetSignal}
              value={stored}
              resourceLabel={t("adminDashboard.adminGroupEdit.rawResourceLabel", {
                defaultValue: "admin group {{id}}",
                id: groupKey,
              })}
              onSave={handleRawSave}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
